import {Control} from 'ol/control';
import {getPointResolution} from 'ol/proj';
import {easeOut} from 'ol/easing';
import {CLASS_UNSELECTABLE, CLASS_CONTROL} from 'ol/css'

/**
 * @const
 * @type {number}
 */
const DEFAULT_DPI = 72;//25.4 / 0.28;

class ScaleBox extends Control {
    /**
    * @param {Object} [opt_options] Control options.
    */
    constructor(opt_options) {
        const options = opt_options || {};

        const className =
          options.className !== undefined
            ? options.className
            : 'ol-scale-box';

        super({
            element: document.createElement('div'),
            render: options.render,
            target: options.target,
        });

        /**
         * @private
         * @type {?import("../View.js").State}
         */
        this.viewState_ = null;

        /**
         * @private
         * @type {boolean}
         */
        this.renderedVisible_ = false;

        /**
         * @private
         * @type {boolean}
         */
        this.readOnly_ = options.readOnly || false;

        /**
         * @private
         * @type {boolean}
         */
        this.useRawInput_ = options.useRawInput || false;

        /**
         * @private
         * @type {string}
         */
        this.label_ = options.label || 'Scale:';

        /**
         * @private
         * @type {string}
         */
        this.template_ = options.template || function(scale) {return scale < 1 ? Math.round(1 / scale) + " : 1" : "1 : " + Math.round(scale)};

        /**
         * @private
         * @type {number|undefined}
         */
        this.dpi_ = options.dpi || undefined;

        /**
         * @private
         * @type {number|undefined}
         */
        this.minZoom_ = options.minZoom || 0;

        /**
         * @type {number}
         * @private
         */
        this.duration_ = options.duration !== undefined ? options.duration : 500;

        if(this.readOnly_){
            this.createScaleReadOnly(className);

        }else if(this.useRawInput_){
            this.createScaleUseRawInput();

        }else{
            this.createScaleForm();
        }

        const containerElement = this.element;
        containerElement.className = className  + ' ' + CLASS_UNSELECTABLE + ' ' + CLASS_CONTROL;
    }

    /**
    * @private
    */
    updateElement_() {
        const viewState = this.viewState_;

        if (!viewState) {
            if (this.renderedVisible_) {
                this.element.style.display = 'none';
                this.renderedVisible_ = false;
            }
            return;
        }

        if(this.readOnly_){
            const mapScale = this.getScaleForResolution();
            this.innerElement_.innerHTML = `<span class="label-prefix">${this.label_}</span>` + ' ' +this.template_(Math.round(mapScale));
        }else{
            const options = this.getOptions();
            const currentResolution = this.getCurrentResolution();
            if(this.innerElement_.options.length==0){
                for (var i = 0; i < options.length; i++) {
                    let option = document.createElement("option");
                    option.value = options[i].res;
                    option.text = this.template_(options[i].scale);
                    if(options[i].res == currentResolution){
                        option.selected = 'selected';
                    }
                    this.innerElement_.appendChild(option);
                }
            }else{
                let options = this.innerElement_.options;
                Object.keys(options).forEach((key, index) => {
                    let item = options[key];
                    if(!!item.getAttribute('hidden')){
                        this.innerElement_.remove(index);
                    }
                });
            }
            let isIntermediate = [...options].filter(opt => opt.value == currentResolution).length==0;
            if(isIntermediate){
                let option = document.createElement("option");
                option.value = currentResolution;
                option.text = this.template_(Math.round(this.getScaleForResolution(currentResolution)));
                option.setAttribute('disabled', 'disabled');
                option.setAttribute('hidden', 'hidden');
                this.innerElement_.appendChild(option);
            }
            this.innerElement_.value = currentResolution;
        }

        if (!this.renderedVisible_) {
            this.element.style.display = '';
            this.renderedVisible_ = true;
        }
    }

    /**
    * Update the scale box element.
    * @param {import("../MapEvent.js").default} mapEvent Map event.
    * @override
    */
    render(mapEvent) {
        const frameState = mapEvent.frameState;
        if (!frameState) {
            this.viewState_ = null;
        } else {
            this.viewState_ = frameState.viewState;
        }
        this.updateElement_();
    }

    createScaleReadOnly(className) {
        const container = this.element;
        let inner = document.createElement('div');
        inner.className = className + '-inner';
        this.innerElement_ = inner;
        container.appendChild(inner);
    }

    createScaleUseRawInput() {
        const container = this.element;
        const select = document.createElement('select');
        select.className = 'form-inline';
        select.addEventListener('change', function(e) { this.onComboChange(e); }.bind(this), false);
        this.innerElement_ = select;
        container.appendChild(select);
    }

    createScaleForm() {
        const container = this.element;
        const form = document.createElement('form');
        form.className = 'form-inline';

        const form_div = document.createElement('div');
        form_div.className = 'form-group form-group-sm';

        const ctrl_label = document.createElement('label');
        ctrl_label.className = 'control-label';

        const label = document.createElement('span');
        label.innerHTML = this.label_;

        const select = document.createElement('select');
        select.className = 'form-control';
        select.addEventListener('change', function(e) { this.onComboChange(e); }.bind(this), false);
        this.innerElement_ = select;

        ctrl_label.appendChild(label);
        form_div.appendChild(ctrl_label);
        form_div.appendChild(select);
        form.appendChild(form_div);

        select.addEventListener('change', function(e) { this.onComboChange(e); }.bind(this), false);
        this.innerElement_ = select;
        container.appendChild(form);
    }

    getOptions() {
        const map = this.getMap();
        if(map){
            let resolutions = map.getView().getResolutions() ?? [];
            resolutions = resolutions
                .filter((el, index) => index >= this.minZoom_)
                .map(res => {return {res, scale: Math.round(this.getScaleForResolution(res))}});
            return resolutions;
        }
        return [];
    }

    onComboChange(evt) {
        let resolution = evt.target.options[evt.target.selectedIndex].value;
        const view = this.getMap().getView();
        const zoom = view.getConstrainedZoom(view.getZoomForResolution(resolution));
        view.animate({
          zoom: zoom,
          duration: this.duration_,
          easing: easeOut,
        });
    }

    /**
    * Returns the appropriate scale for the given resolution and units.
    * @return {number} The appropriate scale.
    */
    getScaleForResolution(res=undefined) {
        // let currentResolution = this.getCurrentResolution();
        // const map = this.getMap();
        // if(map){
        //     let view = map.getView();
        //     const currentZoom = view.getZoom();
        //     const zoom = view.getConstrainedZoom(view.getZoomForResolution(resolution));
        //     console.log(':', view.getZoom(), view.getConstrainedZoom(view.getZoom()), view.getResolutionForZoom(view.getZoom()));
        //     currentResolution = view.getResolutionForZoom(view.getZoom());
        // }
        const resolution = res ? res : this.getCurrentResolution();
        const dpi = this.dpi_ || DEFAULT_DPI;
        const mpu = this.viewState_.projection.getMetersPerUnit();
        const inchesPerMeter = 1000 / 25.4;
        return parseFloat(resolution.toString()) * mpu * inchesPerMeter * dpi;
    }

    getCurrentResolution(){
        const map = this.getMap();
        if(map){
            let view = map.getView();
            return view.getResolutionForZoom(view.getConstrainedZoom(view.getZoom()))
        }else{
            let currentResolution = getPointResolution(
              this.viewState_.projection,
              this.viewState_.resolution,
              this.viewState_.center
            );
            return currentResolution;
        }
    }

}

export default ScaleBox;